/* DropOneWordHostWidget ✦
 *
 * Host Dashboard widget — Setup / Overview tab.
 * Shows:
 *   • total words
 *   • unique words
 *   • most repeated word (with count)
 *   • latest 5 submissions
 *   • "Open the wall ✦" CTA → /one-word/:slug
 *
 * Only renders when the event owns the `drop_one_word_purchased`
 * entitlement (universal across all editions in iter 170, but the
 * gate stays in place so we can soft-remove it for a future tier
 * without ripping out this UI).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { GraphitePanel, MODE_TWO, QuietPress } from './ModeTwoPrimitives';
import DropOneWordDownload from './DropOneWordDownload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ value, label, testId }) => (
  <div data-testid={testId} style={{ textAlign: 'left' }}>
    <div style={{
      fontFamily: MODE_TWO.serif, fontSize: 30, lineHeight: 1, color: MODE_TWO.ink,
      letterSpacing: '-0.01em',
    }}>{value}</div>
    <div style={{
      marginTop: 6, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
      fontSize: 12, color: MODE_TWO.inkMuted, letterSpacing: '0.02em',
    }}>{label}</div>
  </div>
);

const formatRelative = (iso) => {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!t || Number.isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function DropOneWordHostWidget({ event }) {
  const slug = event?.slug || event?.event_id;
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const r = await axios.get(`${API}/one-word/event/${slug}/summary`);
        if (!cancelled) { setSummary(r.data); setBusy(false); }
      } catch (e) {
        if (!cancelled) { setErr(e?.response?.data?.detail || 'Could not load'); setBusy(false); }
      }
    };
    fetchIt();
    const iv = setInterval(fetchIt, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [slug]);

  if (!event || !(event.drop_one_word_purchased || (event.features || []).includes('drop_one_word'))) return null;

  const total = summary?.total || 0;
  const unique = summary?.unique || 0;
  const top = summary?.top_word;
  const latest = summary?.latest || [];

  const wallUrl = `/one-word/${slug}`;

  return (
    <GraphitePanel testId="drop-one-word-host-widget" padding="clamp(22px, 2.6vw, 30px)">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Header line */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: MODE_TWO.gold,
            }}>✦ Tonight's atmosphere</div>
            <div style={{
              marginTop: 6, fontFamily: MODE_TWO.serif, fontSize: 22, color: MODE_TWO.ink,
            }}>Drop One Word ✦</div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <QuietPress to={wallUrl} testId="drop-one-word-open-wall" variant="quiet">
              Open the wall ✦
            </QuietPress>
            <DropOneWordDownload slug={slug} testId="dow-host-download" variant="quiet" label="Save the wall ✦" />
          </div>
        </div>

        {/* Stats row */}
        {busy && !summary && (
          <div data-testid="drop-one-word-loading" style={{
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 13.5, color: MODE_TWO.inkMuted,
          }}>Listening to the room…</div>
        )}
        {err && (
          <div data-testid="drop-one-word-error" style={{
            fontSize: 13, color: '#a3422f',
          }}>{err}</div>
        )}

        {summary && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 18,
            padding: '6px 0',
            borderTop: `1px solid ${MODE_TWO.hairSoft}`,
            borderBottom: `1px solid ${MODE_TWO.hairSoft}`,
          }}>
            <div style={{ paddingTop: 14, paddingBottom: 14 }}>
              <Stat value={total} label={total === 1 ? 'word left' : 'words left'} testId="drop-one-word-total" />
            </div>
            <div style={{ paddingTop: 14, paddingBottom: 14 }}>
              <Stat value={unique} label={unique === 1 ? 'unique feeling' : 'unique feelings'} testId="drop-one-word-unique" />
            </div>
            <div style={{ paddingTop: 14, paddingBottom: 14 }}>
              {top ? (
                <div data-testid="drop-one-word-top">
                  <div style={{
                    fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                    fontSize: 24, color: MODE_TWO.gold, lineHeight: 1.05,
                    letterSpacing: '-0.01em',
                  }}>{top.word}</div>
                  <div style={{
                    marginTop: 6, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                    fontSize: 12, color: MODE_TWO.inkMuted,
                  }}>most repeated · ×{top.count}</div>
                </div>
              ) : (
                <div style={{
                  fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                  fontSize: 13.5, color: MODE_TWO.inkMuted,
                }}>No words yet ✦</div>
              )}
            </div>
          </div>
        )}

        {/* Latest 5 submissions */}
        {summary && latest.length > 0 && (
          <div data-testid="drop-one-word-latest">
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.24em',
              textTransform: 'uppercase', color: MODE_TWO.inkMuted,
              marginBottom: 8,
            }}>Latest in</div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {latest.map((row, i) => (
                <li
                  key={`${row.created_at}-${i}`}
                  data-testid={`drop-one-word-latest-${i}`}
                  style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', gap: 12,
                    padding: '4px 0',
                    borderBottom: i === latest.length - 1 ? 'none' : `1px solid ${MODE_TWO.hairSoft}`,
                  }}
                >
                  <span style={{
                    fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                    fontSize: 16, color: MODE_TWO.ink,
                  }}>{row.word}</span>
                  <span style={{
                    fontSize: 11, color: MODE_TWO.inkMuted, letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                  }}>{formatRelative(row.created_at)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {summary && latest.length === 0 && (
          <div style={{
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 13.5, color: MODE_TWO.inkMuted,
          }}>The room is still quiet — share the wall to invite the first feeling.</div>
        )}
      </div>
    </GraphitePanel>
  );
}
